import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { FormControl } from './FormControl';
import { DROPDOWN_SEARCH_MINIMUM } from '../../../constants';
import {
  autoComplete,
  checkSelectError,
  getDefaultOptionLabel,
  getDefaultOptionValue,
  getFormattedValue,
  onFocus,
  rendererProps,
  selectOnChange,
  translationProps,
} from '../ControlHelpers/SelectUtils.js';
import { isEqual } from 'lodash';
import { selectControlStyles, selectTheme } from './SelectControl.styles';
import { selectComponents } from './SelectComponentWrappers';

export class SelectControl extends FormControl {
  static propTypes = {
    'data-object-attribute': PropTypes.string,
    'data-object-id': PropTypes.string,
    'data-object-type': PropTypes.string,
    'data-select': PropTypes.string,
    children: PropTypes.any,
    clearable: PropTypes.bool,
    clearValue: PropTypes.bool,
    dataValue: PropTypes.any,
    defaultValue: PropTypes.any, // to support legacy default values
    forcePlaceholder: PropTypes.bool,
    isDisabled: PropTypes.bool,
    isMulti: PropTypes.bool,
    joinValues: PropTypes.bool,
    labelKey: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func,
    onValueChange: PropTypes.func,
    options: PropTypes.array,
    placeholderText: PropTypes.string,
    scrollContainerId: PropTypes.string,
    stylesOverride: PropTypes.func,
    valueKey: PropTypes.string,
    valueOverride: PropTypes.bool,
    isLegacyOverride: PropTypes.bool,
  };

  static defaultProps = Object.assign({}, FormControl.defaultProps, {
    placeholderText: '',
    isLegacyOverride: true,
    clearValue: false,
  });

  constructor(props) {
    super(props);

    this.state = {
      hasSetDataValue: false,
      value: props.defaultValue,
      isMenuOpen: false,
    };

    this.select = React.createRef();
  }

  componentDidMount = () => {
    this.setValueOverride();
  };

  componentDidUpdate = prevProps => {
    if (!isEqual(prevProps.dataValue, this.props.dataValue) || !isEqual(prevProps.options, this.props.options)) {
      this.setValueOverride();
    }
    if (prevProps.clearValue !== this.props.clearValue && this.props.clearValue) {
      this.setState({ value: null });
    }
  };

  setValueOverride = () => {
    const { dataValue, isMulti, multi, options, valueKey, valueProperty, isLegacyOverride } = this.props;
    const dataValueAlreadySetOrInexistant = this.state.hasSetDataValue || dataValue === undefined || dataValue === null; // 0 is valid
    const dataValueIsFilledArray = Array.isArray(dataValue) && !dataValue?.length;

    if (!options?.length || dataValueAlreadySetOrInexistant || dataValueIsFilledArray) return;

    const isMultiSelect = multi || isMulti;
    const key = valueKey ?? valueProperty ?? 'value';
    const filteredValue = options?.filter(option => {
      if (!Array.isArray(dataValue)) return String(option?.[key]) === String(dataValue);

      const dataValueArray = dataValue.map(dv => {
        if (!dv || typeof dv === 'string') return dv;
        return dv[key] ?? String(dv);
      });
      return dataValueArray?.includes?.(option?.[key]);
    });
    const selectedValue = isMultiSelect ? filteredValue : (filteredValue?.[0] ?? {});

    this.setState({
      ...this.state,
      hasSetDataValue: true,
      value: selectOnChange(selectedValue, this.props, this.select?.current, isLegacyOverride),
    });
  };

  onChange = (val, skipValueChange) => {
    const { onChange, isMulti, multi } = this.props;
    const skip = typeof skipValueChange === 'boolean' && skipValueChange;
    const value = selectOnChange(val, this.props, this.select?.current, skip);
    const isMultiSelect = isMulti || multi || false;

    this.setState({ ...this.state, value });

    if (onChange) {
      onChange(value);
    }

    if (!isMultiSelect) {
      this.setState({ isMenuOpen: false });
    }
  };

  onBlur = () => {
    if (typeof this.props.onBlur === 'function') {
      this.props.onBlur(this.state.value);
    }
    this.checkError();
  };

  isValid() {
    const input = this.select.wrapper?.querySelector('input');
    if (input) {
      return input.validity.valid;
    }
  }

  onFocus = e => {
    onFocus(e, this.props);
  };

  displayHiddenInput = () => {
    const { name } = this.props;

    // Show/Hide input when Select's internal input's render is toggled to maintain functionality.
    if (name || !this.isSelectDisabled()) {
      return false;
    }

    const { value } = this.state;

    if (Array.isArray(value)) {
      return !value.length;
    }

    return !value;
  };

  checkError() {
    checkSelectError(this.props, this.select?.current);
  }

  handleOnMenuOpen = () => this.setState({ isMenuOpen: true });

  handleOnMenuClose = () => this.setState({ isMenuOpen: false });

  handleOnKeyDown = event => {
    if (event.key === 'Enter' && !this.state.isMenuOpen) {
      this.handleOnMenuOpen();
    }
  };

  isSelectDisabled() {
    const { disabled, isDisabled } = this.props;

    return disabled || isDisabled || false;
  }

  render() {
    const {
      className,
      clearable,
      forcePlaceholder,
      isClearable,
      isFloatOutside,
      isMulti,
      isSearchable,
      multi,
      options,
      placeholder,
      placeholderText,
      required: isRequired,
      searchable,
      stylesOverride,
      ...props
    } = this.props; // eslint-disable-line no-unused-vars
    const { value } = this.state;
    const autocomplete = autoComplete(props);
    // default clearable is true unless explicitly false
    const isClearableSelect = !(clearable === false || isClearable === false);
    const isMultiSelect = multi || isMulti || false;
    const formattedName = isMultiSelect ? props.name + '[]' : props.name;
    const isSearchableSelect =
      !(searchable === false || isSearchable === false) && options?.length > DROPDOWN_SEARCH_MINIMUM;
    const placeholderLabel =
      (!!placeholderText || !!placeholder) && forcePlaceholder ? placeholderText || placeholder : null;
    const classPrefix = `Select-${props.name ? `${props.name}` : ''}`;
    const styles = stylesOverride ? stylesOverride() : selectControlStyles({ isFloatOutside, isMultiSelect, value });
    const Option = props => (
      // eslint-disable-next-line react/prop-types
      <div ref={props.innerRef} {...props.innerProps} {...props}>
        <selectComponents.Option {...props} />
      </div>
    );
    const components = {
      ...selectComponents,
      Option,
    };

    return (
      <>
        <Select
          {...props}
          {...autocomplete}
          {...rendererProps(isMultiSelect, props)}
          {...translationProps}
          aria-labelledby={`${props.name}-label`}
          className={className ?? classPrefix}
          classNamePrefix="Select-"
          components={components}
          getOptionLabel={option => getDefaultOptionLabel(option, this.props)}
          getOptionValue={option => getDefaultOptionValue(option, this.props)}
          hideSelectedOptions={false}
          id={classPrefix}
          inputId={`${classPrefix}-input`}
          instanceId={`${props.name ? `${props.name}-` : ''}value`}
          isClearable={isClearableSelect}
          isDisabled={this.isSelectDisabled()}
          isMulti={isMultiSelect}
          isSearchable={isSearchableSelect}
          name={formattedName}
          onBlur={this.onBlur}
          onChange={this.onChange}
          onFocus={this.onFocus}
          options={options ?? []}
          placeholder={placeholderLabel}
          ref={this.select}
          removeSelected={true}
          required={isRequired}
          scrollMenuIntoView={false}
          styles={styles}
          theme={selectTheme}
          value={getFormattedValue(value, options, this.props)}
          onMenuOpen={this.handleOnMenuOpen}
          onMenuClose={this.handleOnMenuClose}
          menuIsOpen={this.state.isMenuOpen}
          onKeyDown={this.handleOnKeyDown}
        />
        {this.displayHiddenInput() && (
          <input
            type="hidden"
            name={formattedName}
            id={formattedName}
            required={isRequired}
            tabIndex="-1"
            value={value?.value}
          />
        )}
      </>
    );
  }
}
